import { createFileRoute, Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import { Card, CardHeader, CardContent } from '@cortex/web-design-system/components/ui/Card';
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { useQaEvaluationDetail } from '../../hooks/useQaEvaluation.js';
import { ErrorState } from '../../components/feedback/ErrorState';
import { KpiTableSkeleton } from '../../components/feedback/KpiTableSkeleton';
import type { QaLevel, SkillFeedback } from '../../types/api.js';

/**
 * @graph-node {Page}
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business QA評価詳細ページ。個別評価のスコア・レーダーチャート・スキルフィードバック・多言語サマリーを表示
 * @graph-connects useQaEvaluationDetail [calls] QA評価詳細フック呼び出し
 * @graph-connects ErrorState [calls] エラー表示コンポーネント描画
 * @graph-connects KpiTableSkeleton [calls] ローディングスケルトン描画
 * @graph-connects Card [calls] カードコンポーネント描画
 * @graph-connects CardHeader [calls] カードヘッダーコンポーネント描画
 * @graph-connects CardContent [calls] カードコンテンツコンポーネント描画
 * @graph-connects Badge [calls] バッジコンポーネント描画
 * @graph-connects Button [calls] ボタンコンポーネント描画
 * @graph-connects SkillRadarChart [calls] スキルレーダーチャート描画
 * @graph-connects SkillFeedbackCard [calls] スキルフィードバックカード描画
 * @graph-connects getLevelBadge [calls] レベル別バッジ取得
 * @graph-connects getScoreColor [calls] スコア別色取得
 * @graph-connects formatDate [calls] 日付フォーマット
 * @graph-connects getLocalizedText [calls] 多言語テキスト取得
 */
export const Route = createFileRoute('/qa/$id')({
  component: QaDetailPage,
});

/**
 * レベルに応じたバッジを取得
 *
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business QAレベル（senior/middle/junior）に応じた色分けバッジ要素を返却
 * @graph-connects none
 */
function getLevelBadge(level: QaLevel, t: (key: string) => string) {
  switch (level) {
    case 'senior':
      return <Badge variant="success">{t('qa.levels.senior')}</Badge>;
    case 'middle':
      return <Badge variant="info">{t('qa.levels.middle')}</Badge>;
    case 'junior':
      return <Badge variant="default">{t('qa.levels.junior')}</Badge>;
    default:
      return <Badge>{level}</Badge>;
  }
}

/**
 * スコアに応じた色を取得
 *
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business QAスコア（0-10）に応じた色変数（success/info/warning/error）を決定
 * @graph-connects none
 */
function getScoreColor(score: number): string {
  if (score >= 8) return 'var(--color-success)';
  if (score >= 6) return 'var(--color-info)';
  if (score >= 4) return 'var(--color-warning)';
  return 'var(--color-error)';
}

/**
 * 日付をフォーマット
 *
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business 日付文字列を日本語ロケール形式（YYYY/MM/DD）にフォーマット
 * @graph-connects none
 */
function formatDate(dateString: string): string {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  } catch (error) {
    // toLocaleDateString が RangeError 等を投げた場合は元文字列のまま表示
    if (!(error instanceof RangeError)) {
      throw error;
    }
    return dateString;
  }
}

/**
 * 言語に応じたテキストを取得
 *
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business 言語コード（ja/en/vi）に応じたテキストを返却。対象言語が空の場合は日本語にフォールバック
 * @graph-connects none
 */
function getLocalizedText(ja: string, en: string, vi: string, language: string): string {
  switch (language) {
    case 'en':
      return en || ja;
    case 'vi':
      return vi || ja;
    default:
      return ja;
  }
}

/**
 * スキルレーダーチャート
 *
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business QAスキル評価のレーダーチャート。Rechartsでカテゴリ別スコア（0-10）をレーダー図で視覚表示
 * @graph-connects none
 */
function SkillRadarChart({ feedbacks }: { feedbacks: SkillFeedback[] }) {
  // レーダーチャート用にデータを変換
  const chartData = feedbacks.map((fb) => ({
    category: fb.categoryName,
    score: fb.score,
    fullMark: 10,
  }));

  // カスタムツールチップ
  const CustomTooltip = ({
    active,
    payload,
  }: {
    active?: boolean;
    payload?: { payload: { category: string; score: number } }[];
  }) => {
    if (active && payload && payload.length > 0) {
      const data = payload[0].payload;
      return (
        <div className="bg-[var(--color-bg-elevated)] border border-[var(--color-border)] rounded-lg px-3 py-2 shadow-lg">
          <p className="text-sm font-medium text-[var(--color-text-primary)]">{data.category}</p>
          <p className="text-lg font-bold" style={{ color: getScoreColor(data.score) }}>
            {data.score.toFixed(1)}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="w-full h-[300px] sm:h-[350px]">
      <ResponsiveContainer width="100%" height="100%">
        <RadarChart cx="50%" cy="50%" outerRadius="70%" data={chartData}>
          <PolarGrid stroke="var(--color-border)" strokeOpacity={0.5} />
          <PolarAngleAxis
            dataKey="category"
            tick={{
              fill: 'var(--color-text-primary)',
              fontSize: 11,
            }}
            tickLine={false}
          />
          <PolarRadiusAxis
            angle={90}
            domain={[0, 10]}
            tick={{
              fill: 'var(--color-text-muted)',
              fontSize: 10,
            }}
            tickCount={6}
            axisLine={false}
          />
          <Radar
            name="Score"
            dataKey="score"
            stroke="var(--color-accent-primary)"
            fill="var(--color-accent-primary)"
            fillOpacity={0.3}
            strokeWidth={2}
          />
          <Tooltip content={<CustomTooltip />} />
        </RadarChart>
      </ResponsiveContainer>
    </div>
  );
}

/**
 * スキルフィードバックカード
 *
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business QAスキルカテゴリ別のフィードバックカード。スコアバー・評価根拠・改善ポイントを多言語対応で表示
 * @graph-connects none
 */
function SkillFeedbackCard({
  feedback,
  language,
  t,
}: {
  feedback: SkillFeedback;
  language: string;
  t: (key: string) => string;
}) {
  const scoreRationale = getLocalizedText(
    feedback.scoreRationaleJa,
    feedback.scoreRationaleEn,
    feedback.scoreRationaleVi,
    language,
  );

  // rationaleJa から [Evidence] と [Next] の前のテキストのみ抽出
  const mainRationale = scoreRationale.split('\n\n[')[0];

  return (
    <div className="p-4 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-elevated)]">
      <div className="flex items-start justify-between gap-2 mb-3">
        <div>
          <p className="font-medium text-[var(--color-text-primary)]">{feedback.categoryName}</p>
          <p className="text-xs text-[var(--color-text-muted)] mt-0.5">{feedback.categoryId}</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xl font-bold" style={{ color: getScoreColor(feedback.score) }}>
            {feedback.score.toFixed(1)}
          </span>
          {getLevelBadge(feedback.level, t)}
        </div>
      </div>
      <p className="text-sm text-[var(--color-text-secondary)] whitespace-pre-wrap">
        {mainRationale}
      </p>
      {feedback.strengths && feedback.strengths.length > 0 && (
        <div className="mt-3 pt-3 border-t border-[var(--color-border)]">
          <p className="text-xs font-semibold text-[var(--color-text-muted)] mb-1.5">Evidence</p>
          <ul className="space-y-1">
            {feedback.strengths.map((s, i) => (
              <li key={i} className="text-xs text-[var(--color-text-secondary)] flex gap-1.5">
                <span className="text-[var(--color-accent-primary)] shrink-0">•</span>
                <span>{s.description}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {feedback.areasForImprovement && feedback.areasForImprovement.length > 0 && (
        <div className="mt-3 pt-3 border-t border-[var(--color-border)]">
          <p className="text-xs font-semibold text-[var(--color-text-muted)] mb-1.5">Next</p>
          <ul className="space-y-1">
            {feedback.areasForImprovement.map((a, i) => (
              <li key={i} className="text-xs text-[var(--color-text-secondary)] flex gap-1.5">
                <span className="text-[var(--color-warning)] shrink-0">→</span>
                <span>{a.description}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/** @graph-connects pilab-api [calls] PILab APIを呼び出す */
function QaDetailPage() {
  const { t, i18n } = useTranslation();
  const { id } = Route.useParams();

  const { data, isLoading, isError, refetch } = useQaEvaluationDetail(id);

  if (isLoading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div className="flex items-center gap-4">
          <Link to="/qa">
            <Button variant="ghost" size="sm">
              ← {t('actions.back')}
            </Button>
          </Link>
          <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">
            {t('qa.detail.title')}
          </h1>
        </div>
        <KpiTableSkeleton />
      </div>
    );
  }

  if (isError || !data?.data) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div className="flex items-center gap-4">
          <Link to="/qa">
            <Button variant="ghost" size="sm">
              ← {t('actions.back')}
            </Button>
          </Link>
          <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">
            {t('qa.detail.title')}
          </h1>
        </div>
        <ErrorState
          onRetry={() => {
            void refetch();
          }}
        />
      </div>
    );
  }

  const evaluation = data.data;
  const language = i18n.language;

  // 言語に応じたテキストを取得
  const summary = getLocalizedText(
    evaluation.summaryJa,
    evaluation.summaryEn,
    evaluation.summaryVi,
    language,
  );
  const strengths = getLocalizedText(
    evaluation.strengthsJa,
    evaluation.strengthsEn,
    evaluation.strengthsVi,
    language,
  );
  const areasForImprovement = getLocalizedText(
    evaluation.areasForImprovementJa,
    evaluation.areasForImprovementEn,
    evaluation.areasForImprovementVi,
    language,
  );
  const recommendations = getLocalizedText(
    evaluation.recommendationsJa,
    evaluation.recommendationsEn,
    evaluation.recommendationsVi,
    language,
  );

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header with back button */}
      <div className="flex items-center gap-4">
        <Link to="/qa">
          <Button variant="ghost" size="sm">
            ← {t('actions.back')}
          </Button>
        </Link>
        <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">
          {t('qa.detail.title')}
        </h1>
      </div>

      {/* Member Info Card */}
      <Card>
        <CardContent>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2 className="text-xl font-semibold text-[var(--color-text-primary)]">
                {evaluation.userName ?? evaluation.userEmail}
              </h2>
              {evaluation.userName && (
                <p className="text-sm text-[var(--color-text-muted)]">{evaluation.userEmail}</p>
              )}
              <p className="text-sm text-[var(--color-text-secondary)] mt-1">
                {evaluation.quarter} • {t('qa.detail.evaluatedAt')}:{' '}
                {formatDate(evaluation.evaluatedAt)}
              </p>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-center">
                <p className="text-sm text-[var(--color-text-muted)]">{t('qa.overallScore')}</p>
                <p
                  className="text-3xl font-bold"
                  style={{ color: getScoreColor(evaluation.overallScore) }}
                >
                  {evaluation.overallScore.toFixed(1)}
                </p>
              </div>
              <div className="text-center">
                <p className="text-sm text-[var(--color-text-muted)]">{t('qa.level')}</p>
                <div className="mt-1">{getLevelBadge(evaluation.targetLevel, t)}</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Evaluation Period and Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <p className="text-sm text-[var(--color-text-muted)]">{t('qa.detail.periodStart')}</p>
          <p className="text-lg font-semibold text-[var(--color-text-primary)] mt-1">
            {formatDate(evaluation.evaluationPeriodStart)}
          </p>
        </Card>
        <Card>
          <p className="text-sm text-[var(--color-text-muted)]">{t('qa.detail.periodEnd')}</p>
          <p className="text-lg font-semibold text-[var(--color-text-primary)] mt-1">
            {formatDate(evaluation.evaluationPeriodEnd)}
          </p>
        </Card>
        <Card>
          <p className="text-sm text-[var(--color-text-muted)]">{t('qa.detail.criteriaVersion')}</p>
          <p className="text-lg font-semibold text-[var(--color-text-primary)] mt-1">
            {evaluation.criteriaVersion || '-'}
          </p>
        </Card>
      </div>

      {/* Overall Summary */}
      {summary && (
        <Card>
          <CardHeader title={t('qa.detail.summary')} />
          <CardContent>
            <p className="text-[var(--color-text-secondary)] whitespace-pre-wrap leading-relaxed">
              {summary}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Strengths, Areas for Improvement, Recommendations */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Strengths */}
        {strengths && (
          <Card>
            <CardHeader title={t('qa.detail.strengths')} />
            <CardContent>
              <p className="text-[var(--color-text-secondary)] whitespace-pre-wrap text-sm leading-relaxed">
                {strengths}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Areas for Improvement */}
        {areasForImprovement && (
          <Card>
            <CardHeader title={t('qa.detail.areasForImprovement')} />
            <CardContent>
              <p className="text-[var(--color-text-secondary)] whitespace-pre-wrap text-sm leading-relaxed">
                {areasForImprovement}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Recommendations */}
        {recommendations && (
          <Card>
            <CardHeader title={t('qa.detail.recommendations')} />
            <CardContent>
              <p className="text-[var(--color-text-secondary)] whitespace-pre-wrap text-sm leading-relaxed">
                {recommendations}
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Skill Feedbacks */}
      {evaluation.skillFeedbacks.length > 0 && (
        <Card>
          <CardHeader
            title={t('qa.detail.skillFeedbacks')}
            description={t('qa.detail.skillFeedbacksDescription')}
          />
          <CardContent>
            {/* レーダーチャート */}
            <div className="mb-6">
              <SkillRadarChart feedbacks={evaluation.skillFeedbacks} />
            </div>
            {/* スキル別詳細 */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {evaluation.skillFeedbacks.map((feedback) => (
                <SkillFeedbackCard
                  key={feedback.categoryId}
                  feedback={feedback}
                  language={language}
                  t={t}
                />
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
